<template>
  <div style="height: 100%">
    <div
      v-if="!hideHeader"
      class="diff_header"
    >
      <div>{{ preTitle || $t('k_5714949e') }}</div>
      <slot name="rollback" />
      <div>{{ nextTitle || $t('k_d93e74f0') }}</div>
    </div>
    <div
      ref="diffContainer"
      :style="{ height: hideHeader ? '100%' : 'calc(100% - 27px)' }"
      class="diff_container"
    />
  </div>
</template>

<script>
import { $t } from '@/i18n';
import monacoProvider from '@utils/monacoProvider';
import useEditConflict from '@composables/useEditConflict';
import { computed, ref } from '@vue/composition-api';

export default {
  name: 'DetailDiff',
  props: {
    preTitle: {
      type: String,
      default: '',
    },
    nextTitle: {
      type: String,
      default: '',
    },
    /** 隐藏顶部双栏标题（如单栏 unified 模式） */
    hideHeader: {
      type: Boolean,
      default: false,
    },
    originalData: {
      type: String,
      require: true,
      default() {
        return '';
      },
    },
    modifiedData: {
      type: String,
      require: true,
      default() {
        return '';
      },
    },
    type: {
      type: String,
      default() {
        return 'json';
      },
    },
    readOnly: {
      type: Boolean,
      default() {
        return false;
      },
    },
    options: {
      type: Object,
      default: () => {},
    },
  },
  setup() {
    const diffEditor = ref();
    const editor = computed(() => diffEditor.value?.getModifiedEditor());
    const {
      updateConflicts,
    } = useEditConflict({ editor, currentLabel: $t('k_4f3eedd'), incomingLabel: $t('k_eda9c0de') });

    return {
      diffEditor,
      updateConflicts,
    };
  },
  data() {
    return {
      originalModel: null,
      modifiedModel: null,
      contentChangeListener: null,
      diffNavigator: null,
      updateRevision: 0,
      destroyed: false,
      scrollbarTimer: null,
      gutterResizeObserver: null,
      gutterLayoutListener: null,
      fittingGutter: false,
    };
  },
  watch: {
    originalData() {
      this.updateDiff();
    },

    modifiedData() {
      this.updateDiff();
    },

    readOnly(value) {
      this.diffEditor?.updateOptions({ readOnly: value });
    },

    options: {
      deep: true,
      handler() {
        this.diffEditor?.updateOptions(this.getDiffEditorOptions());
        this.scheduleFitInlineOriginalGutter();
      },
    },
  },
  mounted() {
    monacoProvider((monaco) => {
      if (this.destroyed) return;
      this.diffEditor =  monaco.editor.createDiffEditor(this.$refs.diffContainer, {
        enableSplitViewResizing: false,
        readOnly: this.readOnly,
        foldingStrategy: 'indentation',
        cursorBlinking: 'solid',
        automaticLayout: true,
        tabSize: 2,
        language: 'javascript',
        ...this.getDiffEditorOptions(),
      });
      this.updateDiff();
      this.gutterLayoutListener = this.diffEditor.getOriginalEditor().onDidLayoutChange(() => {
        this.scheduleFitInlineOriginalGutter();
      });
      this.gutterResizeObserver = new ResizeObserver(() => this.scheduleFitInlineOriginalGutter());
      if (this.$refs.diffContainer) {
        this.gutterResizeObserver.observe(this.$refs.diffContainer);
      }
      this.scheduleFitInlineOriginalGutter();
      // monaco有bug，滚动条不显示导致滚动条失效，js强制修改style
      this.scrollbarTimer = setTimeout(() => {
        const scrollbars = document.getElementsByClassName('visible scrollbar vertical');
        for (let index = 0;index < scrollbars.length;index ++) {
          scrollbars[index].style.display = 'block';
          scrollbars[index].style.height = '18px';
        }
        this.fitInlineOriginalGutter();
      }, 500);
    });
  },
  beforeDestroy() {
    this.destroyed = true;
    this.updateRevision += 1;
    if (this.scrollbarTimer) clearTimeout(this.scrollbarTimer);
    this.gutterLayoutListener?.dispose();
    this.gutterLayoutListener = null;
    this.gutterResizeObserver?.disconnect();
    this.gutterResizeObserver = null;
    this.disposeDiffResources();
    this.diffEditor?.dispose();
    this.diffEditor = null;
  },
  methods: {
    async updateDiff() {
      this.updateRevision += 1;
      const revision = this.updateRevision;
      const monaco = await monacoProvider();
      if (this.destroyed || revision !== this.updateRevision || !this.diffEditor) return;

      this.disposeDiffResources();
      this.originalModel = monaco.editor.createModel(this.originalData, this.type);
      this.modifiedModel = monaco.editor.createModel(this.modifiedData, this.type);
      this.diffEditor.setModel({
        original: this.originalModel,
        modified: this.modifiedModel,
      });
      this.diffNavigator = monaco.editor.createDiffNavigator(this.diffEditor, {
        followsCaret: true, // resets the navigator state when the user selects something in the editor
        ignoreCharChanges: true, // jump from line to line
      });
      this.diffNavigator.next();
      this.addListener();
      this.updateConflicts(true);
      this.scheduleFitInlineOriginalGutter();
    },
    isInlineDiff() {
      return this.options?.renderSideBySide === false;
    },
    getDiffEditorOptions() {
      if (!this.isInlineDiff()) return { ...this.options };
      // 单栏 diff 仍渲染左右两个 editor：默认 lineNumbersMinChars=5，旧文件那列会留下大块空白
      return {
        ...this.options,
        glyphMargin: false,
        folding: false,
        lineNumbersMinChars: 2,
        lineDecorationsWidth: 12,
        renderOverviewRuler: false,
        minimap: { enabled: false },
      };
    },
    scheduleFitInlineOriginalGutter() {
      requestAnimationFrame(() => this.fitInlineOriginalGutter());
    },
    fitInlineOriginalGutter() {
      if (!this.diffEditor || !this.isInlineDiff() || this.destroyed || this.fittingGutter) return;
      const container = this.$refs.diffContainer;
      if (!container) return;
      const original = this.diffEditor.getOriginalEditor();
      const modified = this.diffEditor.getModifiedEditor();
      const origNode = container.querySelector('.editor.original');
      const modNode = container.querySelector('.editor.modified');
      if (!origNode || !modNode) return;
      const info = original.getLayoutInfo();
      const gutterWidth = Math.max(24, Math.ceil(info.contentLeft || 0));
      const currentWidth = parseFloat(origNode.style.width);
      if (Number.isFinite(currentWidth) && Math.abs(currentWidth - gutterWidth) < 1) return;
      const totalWidth = container.clientWidth;
      const overviewWidth = 30;
      this.fittingGutter = true;
      origNode.style.width = `${gutterWidth}px`;
      origNode.style.left = '0px';
      modNode.style.left = `${gutterWidth}px`;
      modNode.style.width = `${Math.max(0, totalWidth - gutterWidth)}px`;
      original.layout({ width: gutterWidth, height: info.height });
      modified.layout({
        width: Math.max(0, totalWidth - gutterWidth - overviewWidth),
        height: info.height,
      });
      this.fittingGutter = false;
    },
    addListener() {
      const modifiedEditor = this.diffEditor.getModifiedEditor();
      this.contentChangeListener = modifiedEditor.onDidChangeModelContent(() => {
        this.updateConflicts(false);
        this.$emit('change', modifiedEditor.getValue());
      });
    },
    disposeDiffResources() {
      this.contentChangeListener?.dispose();
      this.diffNavigator?.dispose();
      this.diffEditor?.setModel(null);
      this.originalModel?.dispose();
      this.modifiedModel?.dispose();
      this.contentChangeListener = null;
      this.diffNavigator = null;
      this.originalModel = null;
      this.modifiedModel = null;
    },
  },

};
</script>

<style lang="scss" scoped>
.diff_header {
  background: #fff;
  display: flex;
  justify-content: space-around;
  & > div {
    text-align: center;
    line-height: 27px;
  }
}

.diff_container {
  ::v-deep .monaco-diff-editor:not(.side-by-side) .editor.original .monaco-scrollable-element {
    visibility: hidden;
  }
}
</style>
